import { AlertTriangle, PauseCircle } from 'lucide-react';

import { cn } from '@/lib/shadcn/utils';
import { MessageStatusIcon } from '@/modules/messaging/components/MessageStatusIcon';
import type {
  MessageThreadApi,
  MessageWithThreadApi,
} from '@/modules/messaging/types';
import { Avatar, AvatarFallback } from '@/modules/ui/components/avatar/Avatar';
import { Checkbox } from '@/modules/ui/components/checkbox/Checkbox';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';
import { daysSince } from '@/utils/dateUtils';
import { getInitials } from '@/utils/getInitials';

export type SelectItemPayload = {
  id: string;
  checked: boolean;
};

type Message = {
  id: string;
  content?: string | null;
  createdAt?: string;
  sentAt?: string;
  status?: MessageWithThreadApi['status'];
  candidate: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  };
};

interface ThreadListItemProps {
  message: Message;
  unreadCount?: number;
  isHighlighted?: boolean;
  isSelected?: boolean;
  status?: MessageThreadApi['status'];
  onClick?: (id: string) => void;
  onSelect?: (payload: SelectItemPayload) => void;
}

export const ThreadListItem = ({
  message,
  isHighlighted,
  isSelected,
  status,
  onClick,
  onSelect,
}: ThreadListItemProps) => {
  const fullName = `${message.candidate.firstName || ''} ${message.candidate.lastName || ''}`;
  const isCheckboxVisible = isSelected !== undefined;

  const requiresReview = status === 'ReviewRequired';
  const isPaused = status === 'Paused';

  return (
    <div
      className={cn(
        'flex cursor-pointer items-start gap-4 border-b px-6 py-3',
        (requiresReview || isPaused) && 'bg-semantic-540/10',
        isHighlighted && 'bg-primary/10',
      )}
      onClick={() => onClick?.(message.id)}
    >
      <div className="flex items-center justify-center gap-4">
        {isCheckboxVisible && (
          <Checkbox
            checked={isSelected}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(checked) =>
              onSelect?.({ id: message.id, checked: checked === true })
            }
          />
        )}
        <Avatar>
          <AvatarFallback>{getInitials(fullName)}</AvatarFallback>
        </Avatar>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <Subheading variant="h6">
            {fullName || message.candidate.email}
          </Subheading>
          {requiresReview && (
            <AlertTriangle size={16} className="text-semantic-540" />
          )}
          {isPaused && <PauseCircle size={16} className="text-semantic-540" />}
        </div>

        <div className="flex flex-col gap-1">
          <Paragraph variant="body" className="truncate">
            {message.content || 'No messages yet'}
          </Paragraph>

          <div className="flex items-center gap-2">
            {message.status && (
              <div className="flex items-center">
                <MessageStatusIcon status={message.status} />
              </div>
            )}
            {(message.sentAt || message.createdAt) && (
              <Paragraph variant="caption" className="text-muted-foreground">
                {daysSince(message.sentAt || message.createdAt)}
              </Paragraph>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
